/** Provides specialized date and time formatting utilities for task display and scheduling. */
// @ts-check
import {
  parseTimeToMinutes,
  calculateEndTime,
  formatMinutesToTime,
} from '@/utils/timeUtils';
import { translate as t } from '@/i18n/useT';
import { getBcp47Tag } from '@/i18n/getDateFnsLocale';

const localDateStr = (date = new Date()) => date.toLocaleDateString('en-CA');

/**
 * Formats a task date string (YYYY-MM-DD) into a short locale string.
 *
 * Example (nl): `"2026-04-10"` → `"vr. 10/04"`.
 *
 * @param {string|null|undefined} dateString - The task date in YYYY-MM-DD format.
 * @returns {string|null} A formatted string like `"vr. 10/04"`, or `null` when
 *   `dateString` is falsy.
 */
export const formatTaskDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  /** @type {Intl.DateTimeFormatOptions} */
  const options = { weekday: 'short', day: '2-digit', month: '2-digit' };
  return date.toLocaleDateString(getBcp47Tag(), options).replace(/ /g, ' ');
};

/**
 * @param {string|null|undefined} dateString
 * @returns {boolean}
 */
export const isOverdueDate = (dateString) =>
  Boolean(dateString) && dateString < localDateStr();

/**
 * @param {string|null|undefined} dateString
 * @returns {string|null}
 */
export const formatOverdueCaption = (dateString) => {
  if (!isOverdueDate(dateString)) return null;
  const today = new Date(`${localDateStr()}T00:00:00`);
  const due = new Date(`${dateString}T00:00:00`);
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const days = Math.max(
    1,
    Math.round((today.getTime() - due.getTime()) / MS_PER_DAY),
  );
  return t('taskitem.overdueByDays', '{days}d te laat', { days });
};

/**
 * Calculates the next available start time within a planning regime for a given date.
 * Returns the regime start time if no tasks occupy the slot, otherwise returns the
 * latest end time of the tasks already in that slot.
 *
 * @param {string} dateStr - The date in YYYY-MM-DD format.
 * @param {{ start: string, end: string }} regime - Planning regime with start/end in HH:mm.
 * @param {Task[]} allTasks - The full task list to search.
 * @returns {string} The next available time in HH:mm format.
 */
export const calculateNextAvailableTime = (dateStr, regime, allTasks) => {
  const tasksInSameSlot = allTasks.filter(
    (task) =>
      task.date === dateStr &&
      task.time &&
      task.id &&
      parseTimeToMinutes(task.time) >= parseTimeToMinutes(regime.start) &&
      parseTimeToMinutes(task.time) < parseTimeToMinutes(regime.end),
  );

  if (tasksInSameSlot.length === 0) {
    return regime.start;
  }

  const endTimesInMinutes = tasksInSameSlot.map((task) =>
    parseTimeToMinutes(calculateEndTime(task.time, task.duration)),
  );

  const maxEndMinutes = Math.max(...endTimesInMinutes);
  return formatMinutesToTime(maxEndMinutes);
};
